export type VerificationStatus = 'unverified' | 'pending' | 'verified' | 'rejected';

export interface VerificationStatusRow {
  status: string | null | undefined;
  expires_at: string | null | undefined;
}

const KNOWN: ReadonlySet<VerificationStatus> = new Set([
  'unverified',
  'pending',
  'verified',
  'rejected',
]);

/**
 * Pure derivation: takes a verification row + a reference `now` (ms epoch)
 * and returns the effective status. A `verified` row whose `expires_at`
 * has passed degrades back to `unverified` — the server-side webhook is
 * authoritative, but UI must not lie if the client is on an old session.
 */
export const deriveVerificationStatus = (
  row: VerificationStatusRow | null | undefined,
  now: number,
): VerificationStatus => {
  if (!row || !row.status) return 'unverified';
  const status = row.status as VerificationStatus;
  if (!KNOWN.has(status)) return 'unverified';
  if (status !== 'verified') return status;
  if (!row.expires_at) return 'verified';
  const expiresMs = Date.parse(row.expires_at);
  if (Number.isFinite(expiresMs) && expiresMs <= now) return 'unverified';
  return 'verified';
};

export const isVerified = (status: VerificationStatus): boolean =>
  status === 'verified';

/**
 * Single source of truth for "can this user act on verification-gated
 * surfaces" (HOTTR.vip provider apply, HOTTR.tv age-gated content, etc).
 * Today: only 'verified' qualifies. Pending/rejected/unverified all blocked.
 */
export const canActOnGatedSurface = (status: VerificationStatus): boolean =>
  status === 'verified';
